import { faThumbsDown, faThumbsUp, faXmark } from "@fortawesome/pro-regular-svg-icons";
import {
  faThumbsDown as faThumbsDownSelected,
  faThumbsUp as faThumbsUpSelected,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import ReactTextareaAutosize from "react-textarea-autosize";
import { toast } from "react-toastify";
import { FeedbackReason, IMessageFeedbackModel } from "services/api/cplapi/CPLAPI";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { removeMessageFeedback, setMessageFeedback } from "app/modules/aiAssistant/AIAssistantThunks";
import "app/modules/aiAssistant/styles/shared.scss";
import "./MessageFeedback.scss";

const reasons: Record<FeedbackReason, { label: string; testIdName: string }> = {
  [FeedbackReason.WrongData]: { label: "Wrong data", testIdName: "wrong-data" },
  [FeedbackReason.MisunderstoodQuestion]: { label: "Misunderstood my question", testIdName: "misunderstood-question" },
  [FeedbackReason.IncompleteAnswer]: { label: "Incomplete answer", testIdName: "incomplete-answer" },
  [FeedbackReason.NoAnswer]: { label: "Didn't answer", testIdName: "no-answer" },
  [FeedbackReason.TooSlow]: { label: "Too slow", testIdName: "too-slow" },
  [FeedbackReason.Other]: { label: "Other", testIdName: "other" },
};

export type FeedbackControl =
  | "thumbs-up-button"
  | "thumbs-down-button"
  | `reason-${string}-button`
  | "comment-input"
  | "send-button"
  | "close-button";

type Props = {
  messageID: number;
  feedback?: IMessageFeedbackModel;
  getTestId: (control: FeedbackControl) => string;
};

// The rating saves as soon as a thumb is clicked; the reason and comment are an optional follow-up
const MessageFeedback = ({ messageID, feedback, getTestId }: Props) => {
  const thunkDispatch = useThunkDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [draftReason, setDraftReason] = useState<FeedbackReason>();
  const [draftComment, setDraftComment] = useState("");

  // Failures are shown by the app's error alert
  const saveFeedback = async (newFeedback: IMessageFeedbackModel) => {
    setIsSaving(true);
    const result = await thunkDispatch(setMessageFeedback({ messageID, feedback: newFeedback }));
    setIsSaving(false);
    return setMessageFeedback.fulfilled.match(result);
  };

  const removeFeedback = async () => {
    setIsSaving(true);
    const result = await thunkDispatch(removeMessageFeedback(messageID));
    setIsSaving(false);
    if (removeMessageFeedback.fulfilled.match(result)) setIsFormOpen(false);
  };

  const handleThumbClick = async (isPositive: boolean) => {
    if (feedback?.isPositive === isPositive) {
      await removeFeedback();
      return;
    }

    if (await saveFeedback({ isPositive })) {
      setDraftReason(undefined);
      setDraftComment("");
      setIsFormOpen(true);
    }
  };

  const handleSend = async () => {
    const comment = draftComment.trim();
    const saved = await saveFeedback({
      isPositive: feedback?.isPositive,
      reason: feedback?.isPositive ? undefined : draftReason,
      comment: comment === "" ? undefined : comment,
    });

    if (saved) {
      setIsFormOpen(false);
      toast.success("Thanks for your feedback");
    }
  };

  const thumbClassName = (isPositive: boolean) =>
    `assistimo-icon-button assistimo-icon-button--small ${
      feedback?.isPositive === isPositive ? "assistimo-icon-button--selected" : ""
    }`;

  return (
    <>
      <div className="assistimo-feedback-thumbs">
        <button
          data-test-id={getTestId("thumbs-up-button")}
          type="button"
          className={thumbClassName(true)}
          aria-label="Helpful"
          aria-pressed={feedback?.isPositive === true}
          title="Helpful"
          disabled={isSaving}
          onClick={() => handleThumbClick(true)}
        >
          <FontAwesomeIcon icon={feedback?.isPositive === true ? faThumbsUpSelected : faThumbsUp} />
        </button>
        <button
          data-test-id={getTestId("thumbs-down-button")}
          type="button"
          className={thumbClassName(false)}
          aria-label="Not helpful"
          aria-pressed={feedback?.isPositive === false}
          title="Not helpful"
          disabled={isSaving}
          onClick={() => handleThumbClick(false)}
        >
          <FontAwesomeIcon icon={feedback?.isPositive === false ? faThumbsDownSelected : faThumbsDown} />
        </button>
      </div>

      {isFormOpen && feedback && (
        <div className="assistimo-feedback-form">
          <div className="assistimo-feedback-form__header">
            <span className="assistimo-feedback-form__prompt">
              {feedback.isPositive ? "What did you like? (optional)" : "What went wrong? (optional)"}
            </span>
            <button
              data-test-id={getTestId("close-button")}
              type="button"
              className="assistimo-icon-button assistimo-icon-button--small"
              aria-label="Close feedback"
              title="Close"
              onClick={() => setIsFormOpen(false)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          {!feedback.isPositive && (
            <div className="assistimo-feedback-form__reasons">
              {Object.values(FeedbackReason).map((reason) => (
                <button
                  key={reason}
                  data-test-id={getTestId(`reason-${reasons[reason].testIdName}-button`)}
                  type="button"
                  className={`assistimo-feedback-form__reason ${
                    draftReason === reason ? "assistimo-feedback-form__reason--selected" : ""
                  }`}
                  aria-pressed={draftReason === reason}
                  onClick={() => setDraftReason(draftReason === reason ? undefined : reason)}
                >
                  {reasons[reason].label}
                </button>
              ))}
            </div>
          )}

          <div className="assistimo-feedback-form__comment-row">
            <ReactTextareaAutosize
              data-test-id={getTestId("comment-input")}
              className="assistimo-feedback-form__comment"
              minRows={1}
              maxRows={6}
              maxLength={2000}
              placeholder="Add a comment"
              value={draftComment}
              onChange={(e) => setDraftComment(e.target.value)}
            />
            <button
              data-test-id={getTestId("send-button")}
              type="button"
              className="assistimo-button assistimo-button--small assistimo-button--primary"
              disabled={isSaving}
              onClick={handleSend}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MessageFeedback;
